import { AnalyticsCard } from '@/components/analytics/AnalyticsCard'
import { type BadgeVariantType, ChartBadge } from '@/components/analytics/ChartBadge.tsx'
import { ComposedLineBarChart } from '@/components/analytics/ComposedLineBarChart.tsx'
import { createComposedLineBarConfig } from '@/components/analytics/ComposedLineBarConfig.tsx'
import { MAX_ITERATIONS_IN_CHARTS } from '@/constants/ANALYTICS'
import { useUserContext } from '@/contexts/UserContext'
import { useQueryAnalyticsIterations } from '@/requests/queries/useQueryAnalytics'
import { formatIterationDate, formatTooltipHeader } from '@/utils/chart.ts'
import { useMemo } from 'react'

export const getVolatilityMessage = (volatility: number): string => {
  if (volatility <= 10) {
    return 'Generally Stable'
  }
  if (volatility <= 25) {
    return 'Requires Attention'
  }
  return 'Significant Impact on Planning and Stability'
}

export const getVolatilityColorClass = (volatility: number): string => {
  if (volatility <= 10) {
    return 'text-tb-green-900'
  }
  if (volatility <= 25) {
    return 'text-alarm-red-500'
  }
  return 'text-contents-alert'
}

export const VolatilityDetails = () => {
  const { apiKey } = useUserContext()
  const { iterations } = useQueryAnalyticsIterations(apiKey)

  const chartData = useMemo(() => {
    return iterations.slice(-MAX_ITERATIONS_IN_CHARTS)
  }, [iterations])

  const currentIteration = iterations[iterations.length - 1]
  if (!currentIteration) return null

  const message = getVolatilityMessage(currentIteration.volatility)

  const volatilityChart = createComposedLineBarConfig<IterationSummary>({
    lineKey: 'volatility',
    barKey: 'acceptedPoints',
    hideBar: true,
    xFormatter: (iteration: IterationSummary, index: number, all: IterationSummary[]) =>
      formatIterationDate(iteration, index, all),
    tooltipHeaderFormatter: (iteration) => formatTooltipHeader(iteration),
    lineValueFormatter: (value) => `${value} %`,
    barValueFormatter: (value) => `${value} pts`,
    tooltipLabelFormatter: (label) => <ChartBadge variant={label as BadgeVariantType} value={label} />,
    lineLabel: 'Running Volatility',
    barLabel: 'Accepted Points',
  })

  return (
    <AnalyticsCard
      title="Volatility"
      subtitle="Fluctuations in team capacity over time."
      contents={
        <div className="flex flex-col md:flex-row md:justify-between md:items-top gap-3">
          <div>
            <div className={`font-EN-KR-heading1-bold ${getVolatilityColorClass(currentIteration.volatility)}`}>
              {currentIteration.volatility}%
            </div>
            <div className="font-EN-KR-caption-bold text-contents-tertiary">{message}</div>
          </div>

          {chartData.length > 0 && (
            <div className="flex-1 h-[180px] min-w-[240px]" aria-label="Volatility trend chart">
              <ComposedLineBarChart<IterationSummary> data={chartData} config={volatilityChart} height={180} />
            </div>
          )}
        </div>
      }
    />
  )
}
